import { useCollaboratorStore } from '@/stores/collaborators'
import {
  Alert,
  Button,
  Fade,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { EnumAccessLevel } from '../../../@types'
import { Modal } from '../../../components'
import { useInputText } from '../../../hooks'
import services from '../../../services'
import { ModalUpdateProps } from './@types'
import { useData } from './use-data'

export default function ModalUpdate({ open }: ModalUpdateProps) {
  const { ROLES, refetch, users } = useData()
  const { collaboratorID, setCollaboratorID } = useCollaboratorStore()
  const initialValues = { name: '', surname: '' }
  const input = useInputText(initialValues)

  const [accessLevel, setAccessLevel] = useState<EnumAccessLevel>('TEACHER')
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const onAccessLevelChange = (event: SelectChangeEvent) =>
    setAccessLevel(event.target.value.toUpperCase() as EnumAccessLevel)

  const closeModal = () => {
    refetch()
    input.clear()
    setAccessLevel('TEACHER')
    setError(null)
    setLoading(false)
    setCollaboratorID('')
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault()
      if (error) setError(null)
      setLoading(true)
      if (collaboratorID) {
        const { name, surname } = input
        await services.user.update({
          accessLevel,
          name,
          surname,
          id: collaboratorID,
        })
        closeModal()
      }
    } catch (error) {
      setError((error as Error).message)
    } finally {
      refetch()
      setLoading(false)
    }
  }

  useEffect(() => {
    if (users && collaboratorID) {
      const user = users.find(({ id }) => collaboratorID === id)
      if (user) {
        user.name && input.updateInitial('name', user.name)
        user.surname && input.updateInitial('surname', user.surname)
        user.level && input.updateInitial('level', user.level)
        user.email && input.updateInitial('email', user.email)
        user.accessLevel && setAccessLevel(user.accessLevel)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaboratorID, users])

  return (
    <Modal
      open={open}
      onClose={closeModal}
      head={<h2>Atualizar Colaborador</h2>}
    >
      <Fade in={open}>
        <div className="text-xl grid gap-8">
          {loading && <Alert severity="warning">A enviar dados...</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          <form onSubmit={handleSubmit} className="grid gap-4">
            <div className="grid gap-4">
              <TextField
                autoFocus
                label="Primeiro nome"
                name="name"
                value={input.name}
                onChange={input.onChange}
                onBlur={input.onBlur}
                error={!!input.errors.name}
                helperText={input.errors.name}
                disabled={loading}
              />

              <TextField
                label="Último nome"
                name="surname"
                value={input.surname}
                onChange={input.onChange}
                onBlur={input.onBlur}
                error={!!input.errors.surname}
                helperText={input.errors.surname}
                disabled={loading}
              />
            </div>

            <div className="grid gap-4">
              <Select
                value={accessLevel}
                onChange={onAccessLevelChange}
                disabled={loading}
              >
                {ROLES.map((role) => (
                  <MenuItem
                    disabled={role === 'STUDENT' || loading}
                    key={role}
                    value={role}
                  >
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div className="w-max max-w-full ml-auto mt-4 gap-4 flex">
              <Button
                disabled={loading}
                variant="outlined"
                type="reset"
                onClick={closeModal}
              >
                Cancelar
              </Button>
              <Button disabled={loading} type="submit">
                Submeter
              </Button>
            </div>
          </form>
        </div>
      </Fade>
    </Modal>
  )
}
